/**
 * Rendering a captured SIP exchange as a plain text document.
 *
 * The API returns a trace as structured JSON, but what is actually useful to
 * read is the conversation itself: every message in order, each prefixed with
 * enough envelope to say when it happened, which way it went and over what.
 * That is the form an engineer already knows how to read, and the form that
 * pastes usefully into a ticket. Keeping the formatting here rather than in the
 * component makes it directly testable.
 */

/** Full ISO, with the T swapped for a space — unambiguous, and still sortable by eye. */
export function formatTimestamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().replace('T', ' ');
}

function joinParts(parts) {
  return parts.filter((p) => p !== null && p !== undefined && p !== '').join(' · ');
}

/**
 * Which side of the b2bua a message was on, in the words an operator thinks in.
 *
 * The API marks a call's messages `external` (node <-> the customer's PBX or
 * carrier) and `internal` (node <-> the platform the call is bridged to). Those
 * are the node's words for its own flanks; "customer" and "platform" say the
 * same thing without needing to know which end you are standing at. An
 * unrecognised value is passed through rather than dropped — a marker we cannot
 * translate is still a marker.
 */
function legName(leg) {
  switch (String(leg || '').toLowerCase()) {
    case 'external':
      return 'customer';
    case 'internal':
      return 'platform';
    default:
      return leg || null;
  }
}

/**
 * The envelope line above each message. `src`/`dst` are already directional, so
 * the arrow always points src → dst and the IN/OUT tag says which of the two is
 * the node we captured on.
 *
 * The leg sits next to the direction, where it lines up down the left of the
 * document: under `debug` the two legs of a call are interleaved by timestamp,
 * and without a marker to scan for, a bridged call reads as one confused
 * conversation with itself. Absent on a REGISTER, which has only one side, so
 * those envelopes are unchanged.
 */
function messageEnvelope(message) {
  const direction = message?.direction === 'in' ? 'IN ' : message?.direction === 'out' ? 'OUT' : '???';
  const leg = legName(message?.leg);
  const parts = [formatTimestamp(message?.ts), direction];
  if (leg) parts.push(leg.padEnd(8));
  parts.push(message?.transport || '?', `${message?.src || '?'} -> ${message?.dst || '?'}`);
  if (typeof message?.size === 'number') parts.push(`${message.size} bytes`);
  return parts.join('  ');
}

/**
 * The message itself. `raw` is the wire text with digest credentials already
 * redacted at the API; when a node omits it, fall back to the parsed first line
 * and identifiers so the ladder still has a rung.
 */
function messageBody(message) {
  const raw = typeof message?.raw === 'string' ? message.raw.replace(/\s+$/, '') : '';
  if (raw) return message?.truncated ? `${raw}\n… [truncated by the node's capture budget]` : raw;
  const lines = [message?.requestLine || message?.statusLine || '(no message text captured)'];
  if (message?.cseq) lines.push(`CSeq: ${message.cseq}`);
  if (message?.callId) lines.push(`Call-ID: ${message.callId}`);
  return lines.join('\n');
}

function messagesBlock(messages) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return ['  (no messages captured for this exchange)'];
  }
  return messages.flatMap((message) => ['', `== ${messageEnvelope(message)}`, '', messageBody(message)]);
}

function transactionBlock(transaction) {
  const head = joinParts([
    'REGISTER',
    transaction?.outcome,
    transaction?.code != null ? `${transaction.code}` : null,
    typeof transaction?.rttMs === 'number' ? `rtt ${Math.round(transaction.rttMs)} ms` : null,
    transaction?.startedAt ? `started ${formatTimestamp(transaction.startedAt)}` : null,
    transaction?.pinned ? 'pinned' : null,
  ]);
  return [head, ...messagesBlock(transaction?.messages)];
}

function dialogBlock(dialog) {
  const span = dialog?.startedAt
    ? `${formatTimestamp(dialog.startedAt)} → ${dialog?.endedAt ? formatTimestamp(dialog.endedAt) : 'in progress'}`
    : null;
  const head = joinParts([
    `CALL${dialog?.id ? ` ${dialog.id}` : ''}`,
    dialog?.direction,
    dialog?.callId ? `Call-ID ${dialog.callId}` : null,
    // The platform leg is a separate dialog with its own Call-ID. Naming it
    // here is what lets somebody correlate this call with the platform's own
    // logs, which know nothing of the customer's Call-ID.
    dialog?.internalCallId ? `platform Call-ID ${dialog.internalCallId}` : null,
    span,
  ]);
  return [head, ...messagesBlock(dialog?.messages)];
}

/**
 * Keepalives are counters and state changes rather than messages — the volume
 * is high and the diagnostic value is in the outcome, not the bytes.
 */
function keepaliveBlock(keepalive) {
  if (!keepalive || typeof keepalive !== 'object') return [];
  const counters = joinParts([
    keepalive.sent != null ? `sent ${keepalive.sent}` : null,
    keepalive.ok != null ? `ok ${keepalive.ok}` : null,
    keepalive.failed != null ? `failed ${keepalive.failed}` : null,
    keepalive.lastCode != null ? `last ${keepalive.lastCode}` : null,
    typeof keepalive.lastRtt === 'number' ? `${Math.round(keepalive.lastRtt)} ms` : null,
  ]);
  const lines = ['', 'KEEPALIVE (OPTIONS)'];
  if (counters) lines.push(`  ${counters}`);
  (keepalive.transitions || []).forEach((t) => {
    lines.push(`  ${formatTimestamp(t?.at)}  ${t?.state || '?'}${t?.detail ? `  ${t.detail}` : ''}`);
  });
  return lines;
}

/**
 * What the node's ring buffer has thrown away. Reported rather than omitted: a
 * gap in a trace should never be silent.
 */
export function describeEvictions(evictions) {
  if (!evictions || typeof evictions !== 'object') return null;
  const parts = [
    evictions.registerTransactions ? `${evictions.registerTransactions} REGISTER transaction(s)` : null,
    evictions.calls ? `${evictions.calls} call(s)` : null,
    evictions.truncatedMessages ? `${evictions.truncatedMessages} truncated message(s)` : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return `The node's capture buffer has discarded ${parts.join(', ')}.`;
}

/** A one-line label for an index entry, for nodes that don't supply `summary`. */
export function entryLabel(entry) {
  if (entry?.summary) return entry.summary;
  if (entry?.kind === 'call') {
    return joinParts(['Call', entry?.direction, entry?.code != null ? `${entry.code}` : null]);
  }
  return joinParts(['REGISTER', entry?.outcome, entry?.code != null ? `${entry.code}` : null]);
}

/**
 * One fetched exchange as a text document.
 *
 * Walks both arrays a SipTrace can carry, so the same renderer serves a single
 * exchange (what the detail route returns) and a whole-registration trace.
 */
export function traceEntryToText(trace, { registration } = {}) {
  if (!trace || typeof trace !== 'object') return '';

  const who = [registration?.name, registration?.username && registration?.registrar
    ? `${registration.username}@${registration.registrar}`
    : registration?.username]
    .filter(Boolean)
    .join(' — ');

  const lines = [`SIP trace${who ? ` — ${who}` : ''}`];
  const registrationId = trace.registrationId || registration?.id;
  if (registrationId) lines.push(`registration ${registrationId}`);
  const provenance = joinParts([
    trace.node ? `node ${trace.node}` : null,
    trace.capturedAt ? `captured ${formatTimestamp(trace.capturedAt)}` : null,
    trace.fetchedAt ? `fetched ${formatTimestamp(trace.fetchedAt)}` : null,
  ]);
  if (provenance) lines.push(provenance);

  (trace.registerTransactions || []).forEach((transaction) => {
    lines.push('', ...transactionBlock(transaction));
  });
  (trace.calls || []).forEach((dialog) => {
    lines.push('', ...dialogBlock(dialog));
  });
  lines.push(...keepaliveBlock(trace.keepalive));

  const evicted = describeEvictions(trace.evictions);
  if (evicted) lines.push('', evicted);

  return `${lines.join('\n')}\n`;
}

/**
 * The trace routes distinguish "nothing to fetch" from "could not fetch", and
 * each of those from "this deployment cannot fetch at all". Say which, and name
 * the node when the API named one — an operator needs to know whether to look
 * at their SIP provider, at one b2bua node, or at nothing.
 */
export function describeTraceError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  const node = body.node ? ` (node ${body.node})` : '';
  switch (status) {
    case 400:
      return body.message || 'The trace request was rejected as malformed.';
    case 403:
      return 'You do not have permission to read this endpoint, so its SIP trace is not available.';
    case 404:
      return body.message || 'That exchange is no longer held on the node — traces rotate as new activity arrives.';
    case 409:
      return 'No b2bua node has claimed this registration yet, so there is nothing to trace.';
    case 501:
      return `The node holding this registration${node} runs the older stack, which has no tracing API.`;
    case 502:
      return `The owning node${node} refused the request${body.reason ? `: ${body.reason}` : '.'}`;
    case 503:
      return 'SIP tracing is not configured in this deployment.';
    case 504:
      return `The owning node${node} did not answer in time${body.reason ? `: ${body.reason}` : '.'}`;
    default:
      return body.error || body.message || err?.message || 'Failed to fetch the SIP trace.';
  }
}
